import type { ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { API_KEY_SCOPES } from '@gr/core/accounts/identity/api-keys';
import { useT } from '../../../../app/di';
import type { LoadOptions } from '../../../../ui-kit/Combobox/Combobox';
import { FormAlert, FormCheckbox, FormCombobox, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { apiKeyFormDefaults, type ApiKeyFormValues } from '../model/api-keys';

export interface ApiKeyFormProps {
  /** Отправляется кнопкой с form={id} (подвал диалога). */
  id: string;
  /** Администратор выбирает владельца ключа: поиск пользователей (IAM-UC-11, IAM-UC-6.CC-2). */
  loadOwners?: LoadOptions;
  onSubmit(values: ApiKeyFormValues): Promise<ServerOutcome>;
}

/** IAM-UC-9, IAM-UC-11: название ключа и области; у администратора — ещё и владелец. */
export function ApiKeyForm({ id, loadOwners, onSubmit }: ApiKeyFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<ApiKeyFormValues>({
    defaultValues: apiKeyFormDefaults(),
    fields: ['userId', 'name'],
    onSubmit,
  });
  const { control, formState } = form;
  const required = { required: ErrorCode.FieldRequired };
  const disabled = formState.isSubmitting;
  return (
    <form id={id} className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      {loadOwners ? (
        <FormCombobox control={control} name="userId" label={t('identity.apiKeys.owner')} placeholder={t('identity.apiKeys.ownerSearch')} load={loadOwners} rules={required} required disabled={disabled} />
      ) : null}
      <FormTextField control={control} name="name" label={t('identity.apiKeys.name')} hint={t('identity.apiKeys.nameHint')} rules={required} required autoComplete="off" disabled={disabled} />
      <fieldset className="u-stack u-gap-1">
        <legend className="u-small">{t('identity.apiKeys.scopesLabel')}</legend>
        {API_KEY_SCOPES.map((scope) => (
          <FormCheckbox key={scope} control={control} name={`scopes.${scope}`} label={t(`identity.apiKeys.scopes.${scope}`)} disabled={disabled} />
        ))}
        <p className="u-muted u-small">{t('identity.apiKeys.scopesNote')}</p>
      </fieldset>
    </form>
  );
}
